import { Node, ResolvedPos } from "prosemirror-model";
import { Selection, type SelectionBookmark, TextSelection } from "prosemirror-state";
import type { Mappable } from "prosemirror-transform";

export class BlockSelection extends Selection {
	visible = false;

	constructor($anchor: ResolvedPos, $head: ResolvedPos) {
		if ($anchor.depth || $head.depth) throw new RangeError("BlockSelection anchor and head must have depth 0");
		super($anchor, $head);
	}

	map(doc: Node, mapping: Mappable): Selection {
		const $anchor = doc.resolve(mapping.map(this.anchor)),
			$head = doc.resolve(mapping.map(this.head));
		if ($anchor.depth !== 0 || $head.depth !== 0) return TextSelection.between($head, $anchor);
		return new BlockSelection($anchor, $head);
	}

	eq(other: Selection): boolean {
		return other instanceof BlockSelection && other.anchor == this.anchor && other.head == this.head;
	}

	getBookmark(): SelectionBookmark {
		return new BlockBookmark(this.anchor, this.head);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	toJSON(): any {
		return { type: "block", anchor: this.anchor, head: this.head };
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	static fromJSON(doc: Node, json: any) {
		if (typeof json.anchor != "number" || typeof json.head != "number")
			throw new RangeError("Invalid input for BlockSelection.fromJSON");
		return new BlockSelection(doc.resolve(json.anchor), doc.resolve(json.head));
	}

	/// Create a text selection from non-resolved positions.
	static create(doc: Node, anchor: number, head = anchor) {
		let $anchor = doc.resolve(anchor);
		return new this($anchor, head == anchor ? $anchor : doc.resolve(head));
	}
}

Selection.jsonID("block", BlockSelection);

class BlockBookmark implements SelectionBookmark {
	constructor(
		readonly anchor: number,
		readonly head: number,
	) {}

	map(mapping: Mappable) {
		const anchor = mapping.map(this.anchor),
			head = mapping.map(this.head);
		return new BlockBookmark(anchor, head);
	}

	resolve(doc: Node) {
		const $anchor = doc.resolve(this.anchor),
			$head = doc.resolve(this.head);
		if ($anchor.depth !== 0 || $head.depth !== 0) return TextSelection.between($head, $anchor);
		return new BlockSelection($anchor, $head);
	}
}
